import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster, toast } from "@/components/ui/sonner";

// Shell — SaaS root layer (sass.local / dev mode)
import { useHostContext, resolveHostContext } from "@/shell/useHostContext";
import LandingPage from "@/shell/LandingPage";
import DevSelector from "@/shell/DevSelector";
import DevBar from "@/shell/DevBar";
import DemoPage from "@/shell/DemoPage";
import DemoBanner from "@/shell/DemoBanner";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";

// Components
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";

// Pages
import HomePage from "@/pages/HomePage";
import MenuPage from "@/pages/MenuPage";
import ProductPage from "@/pages/ProductPage";
import CustomTacoPage from "@/pages/CustomTacoPage";
import CartPage from "@/pages/CartPage";
import OnboardingWizard from "@/pages/OnboardingWizard";
import OrderTypePage from "@/pages/OrderTypePage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderPage from "@/pages/OrderPage";
import OrdersPage from "@/pages/OrdersPage";
import ProfilePage from "@/pages/ProfilePage";
import LoginPage from "@/pages/LoginPage";
import DemoAccess from "@/pages/DemoAccess";
import StaffLogin from "@/pages/StaffLogin";
import PublicOrderDisplay from "@/pages/PublicOrderDisplay";
import MockGoogleLogin from "@/pages/MockGoogleLogin";


// Admin Pages
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminKitchen from "@/pages/admin/AdminKitchen";
import AdminMenu from "@/pages/admin/AdminMenu";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminOffers from "@/pages/admin/AdminOffers";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminCashReport from "@/pages/admin/AdminCashReport";
import AdminPOS from "@/pages/admin/AdminPOS";
import AdminQRMesas from "@/pages/admin/AdminQRMesas";
import AdminCustomTaco from "@/pages/admin/AdminCustomTaco";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminVeriFactu from "@/pages/admin/AdminVeriFactu";
import AdminQRDisplay from "@/pages/admin/AdminQRDisplay";
import TableView from "@/pages/admin/TableView";
import TableLayoutEditor from "@/pages/admin/TableLayoutEditor";
import AdminBranches from "@/pages/admin/AdminBranches";
import AdminDeliveryHub from "@/pages/admin/AdminDeliveryHub";
import AdminCRM from "@/pages/admin/AdminCRM";
import AdminMarketing from "@/pages/admin/AdminMarketing";

// Driver Pages
import DriverDashboard from "@/pages/driver/DriverDashboard";
import DriverOrders from "@/pages/driver/DriverOrders";
import DriverLogin from "@/pages/DriverLogin";

// New Rider App Pages (PWA)
import RiderLogin from "@/pages/rider/RiderLogin";
import RiderDashboard from "@/pages/rider/RiderDashboard";
import RiderJobs from "@/pages/rider/RiderJobs";
import RiderProfile from "@/pages/rider/RiderProfile";
import RiderWallet from "@/pages/rider/RiderWallet";
import RiderHistory from "@/pages/rider/RiderHistory";

// SuperAdmin Pages
import SuperAdminLogin from "@/pages/superadmin/SuperAdminLogin";
import SuperAdminGuard from "@/pages/superadmin/SuperAdminGuard";
import SuperAdminDashboard from "@/pages/superadmin/SuperAdminDashboard";
import SuperAdminTenants from "@/pages/superadmin/SuperAdminTenants";
import SuperAdminTenantDetail from "@/pages/superadmin/SuperAdminTenantDetail";
import SuperAdminEvents from "@/pages/superadmin/SuperAdminEvents";
import SuperAdminDrivers from "@/pages/superadmin/SuperAdminDrivers";
import SuperAdminOrdersGlobal from "@/pages/superadmin/SuperAdminOrders";
import SuperAdminGlobalMap from "@/pages/superadmin/SuperAdminGlobalMap";
import SuperAdminBilling from "@/pages/superadmin/SuperAdminBilling";
import SuperAdminSubscriptions from "@/pages/superadmin/SuperAdminSubscriptions";
import SuperAdminLogs from "@/pages/superadmin/SuperAdminLogs";
import SuperAdminSettings from "@/pages/superadmin/SuperAdminSettings";
import SuperAdminDeliverySettings from "@/pages/superadmin/SuperAdminDeliverySettings";
import SuperAdminDeliveryHub from "@/pages/superadmin/SuperAdminDeliveryHub";
import SuperAdminPlaceholder from "@/pages/superadmin/SuperAdminPlaceholder";
import SuperAdminPlanes from "@/pages/superadmin/SuperAdminPlanes";
import SuperAdminFeatureFlags from "@/pages/superadmin/SuperAdminFeatureFlags";
import SuperAdminAuditoria from "@/pages/superadmin/SuperAdminAuditoria";

// Legal Pages
import {
  TerminosDeUso,
  PoliticaRiders,
  PoliticaRestaurantes,
  PoliticaPrivacidad,
  PoliticaCookies,
  SLAServicio
} from "@/pages/legal/LegalPages";

// Auth Callback Component
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { processSession, loginWithToken } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const search = location.search;

    // Combine search and hash properly to parse all params
    const searchPart = search.startsWith('?') ? search.substring(1) : search;
    const hashPart = hash.startsWith('#') ? hash.substring(1) : hash;
    const combinedParams = [searchPart, hashPart].filter(Boolean).join('&');
    const params = new URLSearchParams(combinedParams);

    const sessionId = params.get("session_id");
    const token = params.get("token");
    const tenantParam = params.get("tenant");
    const returnTo = params.get("returnTo") || "/menu";
    const demoExpiresAt = params.get("demo_expires_at");

    if (tenantParam) {
      sessionStorage.setItem("dev_mode", "tenant");
      sessionStorage.setItem("dev_subdomain", tenantParam);
    }

    console.log(`[AuthCallback] Processing login. Token: ${token ? 'found' : 'missing'}, SessionId: ${sessionId ? 'found' : 'missing'}`);

    if (token) {
      if (demoExpiresAt) {
        sessionStorage.setItem("demo_mode", "true");
        sessionStorage.setItem("demo_expires_at", demoExpiresAt);
      }

      // DEFINITIVE FIX: Token passed in hash
      loginWithToken(token).then((user) => {
        if (user) {
          console.log(`[AuthCallback] Login success for ${user.email}, navigating to ${returnTo}`);
          toast.success(`Acceso concedido: ${user.name} (${user.role})`);
          
          // Redirect based on user role if returnTo is just /menu (default)
          let finalDest = returnTo;
          if (finalDest.includes("/login") || finalDest.includes("/menu")) {
            // Smart redirect (strip /login and use /menu or role-based dest)
            const baseTenantPath = finalDest.split('/login')[0].split('/menu')[0];
            
            if (user.role === "admin" || user.role === "support") finalDest = baseTenantPath + "/admin";
            else if (user.role === "driver") finalDest = baseTenantPath + "/driver";
            else if (user.role === "cashier") finalDest = baseTenantPath + "/admin/pos";
            else if (user.role === "screen") finalDest = baseTenantPath + "/pedidos-local";
            else finalDest = baseTenantPath + "/menu";
          }
          navigate(finalDest, { replace: true });
        } else {
          console.error("[AuthCallback] Token validation failed");
          navigate("/login", { replace: true });
        }
      });
    } else if (sessionId) {
      // Legacy sessionId support
      processSession(sessionId).then((user) => {
        if (user) {
          if (user.role === "admin" || user.role === "support") navigate("/admin", { replace: true });
          else if (user.role === "driver") navigate("/driver", { replace: true });
          else navigate("/menu", { replace: true });
        } else {
          navigate("/login", { replace: true });
        }
      });
    } else {
      console.warn("[AuthCallback] No login data found in URL");
      navigate("/login", { replace: true });
    }
  }, [location, navigate, processSession, loginWithToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Iniciando sesión...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, roles = [] }) => {
  const { user, isLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(!location.state?.user);

  useEffect(() => {
    if (location.state?.user) {
      setIsChecking(false);
      return;
    }

    const verify = async () => {
      const authenticated = await checkAuth();
      if (!authenticated) {
        if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/driver")) {
          navigate("/staff", { replace: true, state: { from: location.pathname } });
        } else {
          navigate("/login", { replace: true, state: { from: location.pathname } });
        }
      }
      setIsChecking(false);
    };

    verify();
  }, [checkAuth, navigate, location.state]);

  if (isChecking || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentUser = location.state?.user || user;

  if (!currentUser) {
    return null;
  }

  // Si tiene el rol, renderizar. Si no, redirigir vía useEffect para evitar errores de renderizado
  if (roles.length > 0 && !roles.includes(currentUser.role)) {
    let dest = "/";
    if (currentUser.role === "cashier") dest = "/admin/pos";
    else if (currentUser.role === "screen") dest = "/pedidos-local";
    else if (currentUser.role === "driver") dest = "/driver";

    return <Navigate to={dest} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // ── PHASE 4.7: Host-based routing ────────────────────────────────────────────
  // Determina el contexto ANTES de cualquier render de rutas.
  // shell  → sass.local         → SaaS Landing + SuperAdmin
  // dev    → localhost sin mode  → DevSelector
  // tenant → *.sass.local        → Toda la app de restaurante (rutas existentes)
  const { context } = useHostContext();

  // SaaS Shell: Landing + SuperAdmin routes (no tenant app)
  if (context === "shell") {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/onboarding" element={<OnboardingWizard />} />
        {/* SuperAdmin solo accesible desde root */}
        <Route path="/superadmin/login" element={<SuperAdminLogin />} />
        <Route path="/superadmin" element={<SuperAdminGuard><SuperAdminDashboard /></SuperAdminGuard>} />
        <Route path="/superadmin/tenants" element={<SuperAdminGuard><SuperAdminTenants /></SuperAdminGuard>} />
        <Route path="/superadmin/tenants/:company_id" element={<SuperAdminGuard><SuperAdminTenantDetail /></SuperAdminGuard>} />
        <Route path="/superadmin/events" element={<SuperAdminGuard><SuperAdminEvents /></SuperAdminGuard>} />
        <Route path="/superadmin/drivers" element={<SuperAdminGuard><SuperAdminDrivers /></SuperAdminGuard>} />
        <Route path="/superadmin/orders" element={<SuperAdminGuard><SuperAdminOrdersGlobal /></SuperAdminGuard>} />
        <Route path="/superadmin/map" element={<SuperAdminGuard><SuperAdminGlobalMap /></SuperAdminGuard>} />
        <Route path="/superadmin/subscriptions" element={<SuperAdminGuard><SuperAdminSubscriptions /></SuperAdminGuard>} />
        <Route path="/superadmin/billing" element={<SuperAdminGuard><SuperAdminBilling /></SuperAdminGuard>} />
        <Route path="/superadmin/delivery" element={<SuperAdminGuard><SuperAdminDeliveryHub /></SuperAdminGuard>} />
        <Route path="/superadmin/logs" element={<SuperAdminGuard><SuperAdminLogs /></SuperAdminGuard>} />
        <Route path="/superadmin/planes" element={<SuperAdminGuard><SuperAdminPlanes /></SuperAdminGuard>} />
        <Route path="/superadmin/feature-flags" element={<SuperAdminGuard><SuperAdminFeatureFlags /></SuperAdminGuard>} />
        <Route path="/superadmin/auditoria" element={<SuperAdminGuard><SuperAdminAuditoria /></SuperAdminGuard>} />
        <Route path="/superadmin/settings" element={<SuperAdminGuard><SuperAdminSettings /></SuperAdminGuard>} />
        <Route path="/superadmin/settings/delivery" element={<SuperAdminGuard><SuperAdminDeliverySettings /></SuperAdminGuard>} />
        {/* Rider App Routes (Global Network) */}
        <Route path="/rider/login" element={<RiderLogin />} />
        <Route path="/rider" element={<Navigate to="/rider/dashboard" replace />} />
        <Route path="/rider/dashboard" element={<RiderDashboard />} />
        <Route path="/rider/jobs" element={<RiderJobs />} />
        <Route path="/rider/wallet" element={<RiderWallet />} />
        <Route path="/rider/history" element={<RiderHistory />} />
        <Route path="/rider/profile" element={<RiderProfile />} />

        {/* Legal Routes */}
        <Route path="/terminos-de-uso" element={<TerminosDeUso />} />
        <Route path="/politica-riders" element={<PoliticaRiders />} />
        <Route path="/politica-restaurantes" element={<PoliticaRestaurantes />} />
        <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
        <Route path="/politica-cookies" element={<PoliticaCookies />} />
        <Route path="/sla-servicio" element={<SLAServicio />} />

        {/* Catch-all: redirige a landing */}
        <Route path="*" element={<LandingPage />} />
      </Routes>
    );
  }

  // DEV: Selector de contexto (solo localhost sin dev_mode configurado)
  if (context === "dev") {
    return <DevSelector />;
  }

  // TENANT: Toda la app de restaurante (context === "tenant")
  // A partir de aquí, código original sin cambios ─────────────────────────────

  // Check for login signals in URL hash/search BEFORE rendering routes (tenant context)
  const hasToken = location.hash?.includes("token=") || location.search?.includes("token=");
  const hasSession = location.hash?.includes("session_id=") || location.search?.includes("session_id=");
  const isAuthCallback = location.pathname === "/auth/callback" || hasToken || hasSession;

  console.log(`[AppRouter] Path: ${location.pathname}, AuthCallback: ${isAuthCallback}, HasToken: ${hasToken}`);

  if (isAuthCallback) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/menu" element={<MenuPage />} />
      <Route path="/menu/:itemId" element={<ProductPage />} />
      <Route path="/crea-tu-taco" element={<CustomTacoPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/driver" element={<DriverLogin />} />
      {/* Bridge can match with or without /auth prefix depending on basename resolution */}
      <Route path="/auth/google/mock" element={<MockGoogleLogin />} />
      <Route path="/google/mock" element={<MockGoogleLogin />} />
      <Route path="/demo" element={<DemoAccess />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/staff" element={<StaffLogin />} />

      {/* Cart is public, order-type and checkout require auth */}
      <Route path="/cart" element={<CartPage />} />
      <Route path="/onboarding" element={<OnboardingWizard />} />
      <Route path="/order-type" element={<ProtectedRoute><OrderTypePage /></ProtectedRoute>} />
      <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
      <Route path="/order/:orderId" element={<OrderPage />} />
      <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/pedidos-local" element={<PublicOrderDisplay />} />

      {/* Admin Routes - allow cashier and kitchen for their specific pages */}
      <Route path="/admin" element={<ProtectedRoute roles={["admin", "support"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/kitchen" element={<ProtectedRoute roles={["admin", "kitchen", "support"]}><AdminKitchen /></ProtectedRoute>} />
      <Route path="/admin/menu" element={<ProtectedRoute roles={["admin", "support"]}><AdminMenu /></ProtectedRoute>} />
      <Route path="/admin/orders" element={<ProtectedRoute roles={["admin", "cashier", "support"]}><AdminOrders /></ProtectedRoute>} />
      <Route path="/admin/orders/:orderId" element={<ProtectedRoute roles={["admin", "cashier", "support"]}><AdminOrders /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={["admin", "support"]}><AdminUsers /></ProtectedRoute>} />        <Route path="/admin/integrations" element={<ProtectedRoute roles={["admin", "support"]}><AdminIntegrations /></ProtectedRoute>} />      <Route path="/admin/verifactu" element={<ProtectedRoute roles={["admin", "support"]}><AdminVeriFactu /></ProtectedRoute>} />      <Route path="/admin/cash" element={<ProtectedRoute roles={["admin", "cashier", "support"]}><AdminCashReport /></ProtectedRoute>} />
      <Route path="/admin/pos" element={<ProtectedRoute roles={["admin", "cashier", "support"]}><AdminPOS /></ProtectedRoute>} />
      <Route path="/admin/qr-mesas" element={<ProtectedRoute roles={["admin", "support"]}><AdminQRMesas /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute roles={["admin", "support"]}><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/offers" element={<ProtectedRoute roles={["admin", "support"]}><AdminOffers /></ProtectedRoute>} />
      <Route path="/admin/custom-taco" element={<ProtectedRoute roles={["admin", "support"]}><AdminCustomTaco /></ProtectedRoute>} />
      <Route path="/admin/qr-display" element={<ProtectedRoute roles={["admin", "support"]}><AdminQRDisplay /></ProtectedRoute>} />
      <Route path="/admin/tables" element={<ProtectedRoute roles={["admin", "cashier", "support"]}><TableView /></ProtectedRoute>} />
      <Route path="/admin/tables/editor" element={<ProtectedRoute roles={["admin", "support"]}><TableLayoutEditor /></ProtectedRoute>} />
      <Route path="/admin/branches" element={<ProtectedRoute roles={["admin", "support"]}><AdminBranches /></ProtectedRoute>} />
      <Route path="/admin/delivery" element={<ProtectedRoute roles={["admin", "cashier", "support"]}><AdminDeliveryHub /></ProtectedRoute>} />
      <Route path="/admin/customers" element={<ProtectedRoute roles={["admin", "support"]}><AdminCRM /></ProtectedRoute>} />
      <Route path="/admin/marketing" element={<ProtectedRoute roles={["admin", "support"]}><AdminMarketing /></ProtectedRoute>} />

      {/* Driver Routes */}
      <Route path="/driver" element={<ProtectedRoute roles={["driver", "admin", "support"]}><DriverDashboard /></ProtectedRoute>} />
      <Route path="/driver/orders" element={<ProtectedRoute roles={["driver", "admin", "support"]}><DriverOrders /></ProtectedRoute>} />

      {/* New Rider App Routes (PWA Delivery) */}
      <Route path="/rider/login" element={<RiderLogin />} />
      <Route path="/rider" element={<Navigate to="/rider/dashboard" replace />} />
      <Route path="/rider/dashboard" element={<RiderDashboard />} />
      <Route path="/rider/jobs" element={<RiderJobs />} />
      <Route path="/rider/wallet" element={<RiderWallet />} />
      <Route path="/rider/history" element={<RiderHistory />} />
      <Route path="/rider/profile" element={<RiderProfile />} />

      {/* SuperAdmin Routes (token propio, no mezcla con auth normal) */}
      <Route path="/superadmin/login" element={<SuperAdminLogin />} />
      <Route path="/superadmin" element={<SuperAdminGuard><SuperAdminDashboard /></SuperAdminGuard>} />
      <Route path="/superadmin/tenants" element={<SuperAdminGuard><SuperAdminTenants /></SuperAdminGuard>} />
      <Route path="/superadmin/tenants/:company_id" element={<SuperAdminGuard><SuperAdminTenantDetail /></SuperAdminGuard>} />
      <Route path="/superadmin/events" element={<SuperAdminGuard><SuperAdminEvents /></SuperAdminGuard>} />
      <Route path="/superadmin/drivers" element={<SuperAdminGuard><SuperAdminDrivers /></SuperAdminGuard>} />
      <Route path="/superadmin/map" element={<SuperAdminGuard><SuperAdminGlobalMap /></SuperAdminGuard>} />
      <Route path="/superadmin/orders" element={<SuperAdminGuard><SuperAdminOrdersGlobal /></SuperAdminGuard>} />
      <Route path="/superadmin/subscriptions" element={<SuperAdminGuard><SuperAdminSubscriptions /></SuperAdminGuard>} />
      <Route path="/superadmin/billing" element={<SuperAdminGuard><SuperAdminBilling /></SuperAdminGuard>} />
      <Route path="/superadmin/delivery" element={<SuperAdminGuard><SuperAdminDeliveryHub /></SuperAdminGuard>} />
      <Route path="/superadmin/logs" element={<SuperAdminGuard><SuperAdminLogs /></SuperAdminGuard>} />
      <Route path="/superadmin/planes" element={<SuperAdminGuard><SuperAdminPlanes /></SuperAdminGuard>} />
      <Route path="/superadmin/feature-flags" element={<SuperAdminGuard><SuperAdminFeatureFlags /></SuperAdminGuard>} />
      <Route path="/superadmin/auditoria" element={<SuperAdminGuard><SuperAdminAuditoria /></SuperAdminGuard>} />
      <Route path="/superadmin/settings" element={<SuperAdminGuard><SuperAdminSettings /></SuperAdminGuard>} />
      
      {/* Catch-all for better UX */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  // Resolve context statically for basename
  const { hubPrefix } = resolveHostContext(
    window.location.hostname, 
    window.location.pathname, 
    window.location.search
  );

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={hubPrefix || ""}>
        <AuthProvider>
          <CartProvider>
            <AppRouter />
            <Toaster position="top-center" richColors />
            <PWAInstallPrompt />
            {/* Demo Banner: muestra timer y CTA cuando hay sesión demo */}
            <DemoBanner />
            {/* DevBar: solo visible en localhost, nunca en producción */}
            <DevBar />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
